import { subYears } from 'date-fns';
import { useEffect } from 'react';
import { createSelector } from 'reselect';
import { useRegisterPersistedStore } from '@/stores/PersistenceProvider';
import { type SetStoreState, createStore, createStoreInstance } from '@/stores/createStore';
import type {
  ImpactReportCategory,
  ImpactReportColumnConfiguration,
  ImpactReportConfiguration,
  ImpactReportDashboardContent,
  ImpactReportFilterModel,
  ImpactReportLayout,
  ImpactReportUseCase,
  ImpactTotals,
  MapZoomSettings,
  PredefinedDateRangeOptions,
} from '@/impactReports/impactReport.types';
import type { ColumnDefinitionOutputV1 } from '@/sdk/model/ColumnDefinitionOutputV1';

export interface ImpactReportState {
  tableId: string | undefined;
  forceTableReload: boolean;
  categories: ImpactReportCategory[] | undefined;
  startDate: string | undefined;
  endDate: string | undefined;
  layoutDefinition: ImpactReportDashboardContent[];
  layouts: ImpactReportLayout[];
  selectedLayoutId: string | undefined;
  aggregationColumn: string | undefined;
  biggestImpact: number;
  useCaseCount: number;
  overallImpact: number;
  filteredImpact: number;
  displayEditModal: boolean;
  showAssetPath: boolean;
  showSite: boolean;
  showLocation: boolean;
  showImpact: boolean;
  updateNominations: boolean;
  definedTimeRange: string | undefined;
  kpiColumns: ColumnDefinitionOutputV1[];
  expectedAnnualKpiColumns: ColumnDefinitionOutputV1[];
  expectedAnnualKpiVisibility: Record<string, boolean>;
  largestKPIImpacts: ImpactTotals;
  filteredTotals: ImpactTotals;
  absoluteTotals: ImpactTotals;
  tableData: ImpactReportUseCase[];
  filteredLocation: string | undefined;
  editingTileId: string | undefined;
  columnConfiguration: ImpactReportColumnConfiguration;
  impactByLocation: ImpactReportUseCase[];
  mapZoom: MapZoomSettings | undefined;
  filterModel: ImpactReportFilterModel;
}

export type ImpactReportDehydratedState = Pick<ImpactReportState, 'startDate' | 'endDate' | 'definedTimeRange'>;

const buildInitialState = (): ImpactReportState => {
  const now = new Date();

  return {
    tableId: undefined,
    categories: undefined,
    startDate: subYears(now, 1).toISOString(),
    endDate: now.toISOString(),
    forceTableReload: false,
    layoutDefinition: [],
    layouts: [],
    selectedLayoutId: undefined,
    aggregationColumn: undefined,
    biggestImpact: 0,
    useCaseCount: 0,
    overallImpact: 0,
    filteredImpact: 0,
    displayEditModal: false,
    showAssetPath: true,
    showSite: false,
    showLocation: true,
    updateNominations: false, // to make sure they are fetched initially
    definedTimeRange: 'pastYear',
    showImpact: true,
    kpiColumns: [],
    expectedAnnualKpiColumns: [],
    expectedAnnualKpiVisibility: {},
    absoluteTotals: {},
    filteredTotals: {},
    largestKPIImpacts: {},
    tableData: [],
    filteredLocation: undefined,
    editingTileId: undefined,
    columnConfiguration: {},
    impactByLocation: [],
    mapZoom: undefined,
    filterModel: null,
  };
};

const impactReportActionsFactory = (set: SetStoreState<ImpactReportState>) => ({
  setTableId: (tableId: string | undefined) =>
    set((draft: ImpactReportState) => {
      draft.tableId = tableId;
    }),
  setCategories: (categories: ImpactReportCategory[] | undefined) =>
    set((draft: ImpactReportState) => {
      draft.categories = categories;
    }),
  setStartDate: (startDate: string | undefined) =>
    set((draft: ImpactReportState) => {
      draft.startDate = startDate;
    }),
  setEndDate: (endDate: string | undefined) =>
    set((draft: ImpactReportState) => {
      draft.endDate = endDate;
    }),
  setLayoutDefinition: (layoutDefinition: ImpactReportDashboardContent[]) =>
    set((draft: ImpactReportState) => {
      draft.layoutDefinition = layoutDefinition;
    }),
  removeContent: (i: string) =>
    set((draft: ImpactReportState) => {
      draft.layoutDefinition = draft.layoutDefinition.filter((content) => content.i !== i);
    }),
  setForceTableReload: (forceTableReload: boolean) =>
    set((draft: ImpactReportState) => {
      draft.forceTableReload = forceTableReload;
    }),
  setAggregationColumn: (aggregationColumn: string | undefined) =>
    set((draft: ImpactReportState) => {
      draft.aggregationColumn = aggregationColumn;
    }),
  setBiggestImpact: (biggestImpact: number) =>
    set((draft: ImpactReportState) => {
      draft.biggestImpact = biggestImpact;
    }),
  setUseCaseCount: (useCaseCount: number) =>
    set((draft: ImpactReportState) => {
      draft.useCaseCount = useCaseCount;
    }),
  setOverallImpact: (overallImpact: number) =>
    set((draft: ImpactReportState) => {
      draft.overallImpact = overallImpact;
    }),
  setFilteredImpact: (filteredImpact: number) =>
    set((draft: ImpactReportState) => {
      draft.filteredImpact = filteredImpact;
    }),
  setDisplayEditModal: (displayEditModal: boolean) =>
    set((draft: ImpactReportState) => {
      draft.displayEditModal = displayEditModal;
    }),
  setShowAssetPath: (showAssetPath: boolean) =>
    set((draft: ImpactReportState) => {
      draft.showAssetPath = showAssetPath;
    }),
  setShowSite: (showSite: boolean) =>
    set((draft: ImpactReportState) => {
      draft.showSite = showSite;
    }),
  setShowLocation: (showLocation: boolean) =>
    set((draft: ImpactReportState) => {
      draft.showLocation = showLocation;
    }),
  setShowImpact: (showImpact: boolean) =>
    set((draft: ImpactReportState) => {
      draft.showImpact = showImpact;
    }),
  setLayouts: (layouts: ImpactReportLayout[]) =>
    set((draft: ImpactReportState) => {
      draft.layouts = layouts;
    }),
  setSelectedLayoutId: (selectedLayoutId: string | undefined) =>
    set((draft: ImpactReportState) => {
      draft.selectedLayoutId = selectedLayoutId;
    }),
  setUpdateNominations: (updateNominations: boolean) =>
    set((draft: ImpactReportState) => {
      draft.updateNominations = updateNominations;
    }),
  setDefinedTimeRange: (definedTimeRange: PredefinedDateRangeOptions | undefined) =>
    set((draft: ImpactReportState) => {
      draft.definedTimeRange = definedTimeRange;
    }),
  setTableData: (tableData: ImpactReportUseCase[]) =>
    set((draft: ImpactReportState) => {
      draft.tableData = tableData;
    }),
  setImpactByLocation: (tableData: ImpactReportUseCase[]) =>
    set((draft: ImpactReportState) => {
      draft.impactByLocation = tableData;
    }),
  setKpiColumns: (kpiColumns: ColumnDefinitionOutputV1[]) =>
    set((draft: ImpactReportState) => {
      draft.kpiColumns = kpiColumns;
    }),
  setExpectedAnnualKpiColumns: (expectedAnnualKpiColumns: ColumnDefinitionOutputV1[]) =>
    set((draft: ImpactReportState) => {
      draft.expectedAnnualKpiColumns = expectedAnnualKpiColumns;
    }),
  setExpectedAnnualKpiVisibility: (expectedAnnualKpiVisibility: Record<string, boolean>) =>
    set((draft: ImpactReportState) => {
      draft.expectedAnnualKpiVisibility = expectedAnnualKpiVisibility;
    }),
  setAbsoluteTotals: (absoluteTotals: ImpactTotals) =>
    set((draft: ImpactReportState) => {
      draft.absoluteTotals = absoluteTotals;
    }),
  setFilteredTotals: (filteredTotals: ImpactTotals) =>
    set((draft: ImpactReportState) => {
      draft.filteredTotals = filteredTotals;
    }),
  setLargestKpiImpacts: (largestKPIImpacts: ImpactTotals) =>
    set((draft: ImpactReportState) => {
      draft.largestKPIImpacts = largestKPIImpacts;
    }),
  setFilteredLocation: (filteredLocation: string | undefined) =>
    set((draft: ImpactReportState) => {
      draft.filteredLocation = filteredLocation;
    }),
  setEditingTileId: (editingTileId: string | undefined) =>
    set((draft: ImpactReportState) => {
      draft.editingTileId = editingTileId;
    }),
  setColumnConfiguration: (columnConfiguration: ImpactReportColumnConfiguration) =>
    set((draft: ImpactReportState) => {
      draft.columnConfiguration = columnConfiguration;
    }),
  setMapZoom: (mapZoom: MapZoomSettings | undefined) =>
    set((draft: ImpactReportState) => {
      draft.mapZoom = mapZoom;
    }),
  setFilterModel: (filterModel: ImpactReportFilterModel) =>
    set((draft: ImpactReportState) => {
      draft.filterModel = filterModel;
    }),
  setValueCaptureDateRange: (startDate: string, endDate: string) =>
    set((draft: ImpactReportState) => {
      draft.startDate = startDate;
      draft.endDate = endDate;
    }),
  reset: (initialState: ImpactReportState) =>
    set((draft: ImpactReportState) => {
      Object.assign(draft, initialState);
    }),
});

const [ImpactReportStoreProvider, useImpactReportStoreInstance, useImpactReportSelector] = createStore(
  'ImpactReport',
  buildInitialState(),
  impactReportActionsFactory,
);

export { ImpactReportStoreProvider };

/**
 * Creates a new store instance for tests that must assert exact store state or when
 * porting Flux store tests that call actions imperatively.
 *
 * Prefer `<TestStoreProvider>` or `<*StoreProvider>` in tests and validate behavior
 * through UI interactions + DOM assertions whenever possible.
 *
 * NOTE: Only use this function in **test files**.
 *
 * @deprecated This function is ONLY for legacy migration from Flux/Baobab stores. Prefer
 * `<TestStoreProvider>` or `<*StoreProvider>` with UI-driven assertions instead.
 */
export function createTestImpactReportStore(overrides?: Partial<ImpactReportState>) {
  if (process.env.NODE_ENV !== 'test') {
    throw new Error('createTestImpactReportStore() should only be used in tests.');
  }

  return createStoreInstance({ ...buildInitialState(), ...overrides }, impactReportActionsFactory);
}

export const selectImpactReportConfiguration = createSelector(
  (state: ImpactReportState) => state.showAssetPath,
  (state: ImpactReportState) => state.showSite,
  (state: ImpactReportState) => state.showLocation,
  (state: ImpactReportState) => state.showImpact,
  (state: ImpactReportState) => state.expectedAnnualKpiVisibility,
  (showAssetPath, showSite, showLocation, showImpact, expectedAnnualKpiVisibility): ImpactReportConfiguration => ({
    showAssetPath,
    showSite,
    showLocation,
    showImpact,
    expectedAnnualKpiVisibility,
  }),
);

export const dehydrateImpactReportState = (state: ImpactReportState): ImpactReportDehydratedState => ({
  startDate: state.startDate,
  endDate: state.endDate,
  definedTimeRange: state.definedTimeRange,
});

type DisplayValueProperties = keyof Pick<
  ImpactReportState,
  'overallImpact' | 'biggestImpact' | 'filteredImpact' | 'useCaseCount'
>;

export const useImpactReportActions = () => useImpactReportStoreInstance().actions;
export const useImpactReportBoxDisplayValue = ({
  property,
  adjustedProperty,
  actualValue,
}: {
  property: DisplayValueProperties | string;
  adjustedProperty: string;
  actualValue?: string;
}): number | undefined =>
  useImpactReportSelector((state) => {
    const isOverall = actualValue === 'overall' || property === 'overallImpact';
    const isLargest = actualValue === 'largest' || property === 'biggestImpact';

    if (isOverall) {
      return state.absoluteTotals[adjustedProperty];
    }

    if (isLargest) {
      return state.largestKPIImpacts[adjustedProperty];
    }

    if (property === 'filteredImpact') {
      return state.filteredImpact;
    }

    if (property === 'useCaseCount') {
      return state.useCaseCount;
    }

    return undefined;
  });
export const useImpactReportTableId = () => useImpactReportSelector((state) => state.tableId);
export const useImpactReportCategories = () => useImpactReportSelector((state) => state.categories);
export const useImpactReportStartDate = () => useImpactReportSelector((state) => state.startDate);
export const useImpactReportEndDate = () => useImpactReportSelector((state) => state.endDate);
export const useImpactReportLayoutDefinition = () => useImpactReportSelector((state) => state.layoutDefinition);
export const useImpactReportLayouts = () => useImpactReportSelector((state) => state.layouts);
export const useImpactReportSelectedLayoutId = () => useImpactReportSelector((state) => state.selectedLayoutId);
export const useImpactReportAggregationColumn = () => useImpactReportSelector((state) => state.aggregationColumn);
export const useImpactReportBiggestImpact = () => useImpactReportSelector((state) => state.biggestImpact);
export const useImpactReportUseCaseCount = () => useImpactReportSelector((state) => state.useCaseCount);
export const useImpactReportOverallImpact = () => useImpactReportSelector((state) => state.overallImpact);
export const useImpactReportFilteredImpact = () => useImpactReportSelector((state) => state.filteredImpact);
export const useImpactReportDisplayEditModal = () => useImpactReportSelector((state) => state.displayEditModal);
export const useImpactReportShowAssetPath = () => useImpactReportSelector((state) => state.showAssetPath);
export const useImpactReportShowSite = () => useImpactReportSelector((state) => state.showSite);
export const useImpactReportShowLocation = () => useImpactReportSelector((state) => state.showLocation);
export const useImpactReportShowImpact = () => useImpactReportSelector((state) => state.showImpact);
export const useImpactReportUpdateNominations = () => useImpactReportSelector((state) => state.updateNominations);
export const useImpactReportDefinedTimeRange = () => useImpactReportSelector((state) => state.definedTimeRange);
export const useImpactReportKpiColumns = () => useImpactReportSelector((state) => state.kpiColumns);
export const useImpactReportExpectedAnnualKpiColumns = () =>
  useImpactReportSelector((state) => state.expectedAnnualKpiColumns);
export const useImpactReportExpectedAnnualKpiVisibility = () =>
  useImpactReportSelector((state) => state.expectedAnnualKpiVisibility);
export const useImpactReportLargestKpiImpacts = () => useImpactReportSelector((state) => state.largestKPIImpacts);
export const useImpactReportFilteredTotals = () => useImpactReportSelector((state) => state.filteredTotals);
export const useImpactReportAbsoluteTotals = () => useImpactReportSelector((state) => state.absoluteTotals);
export const useImpactReportTableData = () => useImpactReportSelector((state) => state.tableData);
export const useImpactReportFilteredLocation = () => useImpactReportSelector((state) => state.filteredLocation);
export const useImpactReportEditingTileId = () => useImpactReportSelector((state) => state.editingTileId);
export const useImpactReportColumnConfiguration = () => useImpactReportSelector((state) => state.columnConfiguration);
export const useImpactReportImpactByLocation = () => useImpactReportSelector((state) => state.impactByLocation);
export const useImpactReportMapZoom = () => useImpactReportSelector((state) => state.mapZoom);
export const useImpactReportFilterModel = () => useImpactReportSelector((state) => state.filterModel);
export const useImpactReportForceTableReload = () => useImpactReportSelector((state) => state.forceTableReload);
export const useImpactReportConfiguration = () => useImpactReportSelector(selectImpactReportConfiguration);

export const usePersistImpactReportStore = () => {
  const register = useRegisterPersistedStore();
  const store = useImpactReportStoreInstance();

  useEffect(() => {
    return register({
      storeName: 'sqImpactReportStore',
      persistenceLevel: 'WORKBENCH',
      store,
      dehydrate: dehydrateImpactReportState,
      reset: (initialState) => {
        store.setState(initialState, true);
      },
    });
  }, [register, store]);
};
